import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE } from '../Common/API/ItemsGetters'
import { StateSetter } from '../Common/Types/Types'
import { encrypt } from '../Common/Utils/Security'
import {
    LANGUAGE_DEFAULT,
    REPORT_EDIT_PERMS,
    REPORTS_READONLY,
    SERVER_ALL_VALUE,
    SERVER_NONE_CRI,
    SERVER_NONE_VALUE
} from './Constants'
import {
    mapNonAdminPermissions,
    mapOtherPermissions,
    mapValuesToPermissions,
    useMapPermissionsToFormValues
} from './CustomiseRole/RoleMapper'
import { FormEndpoint } from './ExtensionEndpoint/ExtensionEndpoint'
import { FormPermissions } from './Permissions/Permissions'
import { ArrayUtils } from '../../NonReact/utils/ArrayUtils'
import { BackendPermissionNames } from '../Common/Constants/Permissions'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { DEFAULT_TIMEZONE } from '../TimezoneModal/Constants/Constants'
import { ApplicationUser } from '../../NonReact/common/Types/AddUser/AddUserTypes'
import { AdminUserBody } from '../../NonReact/common/Types/Types'
import { ReportingAccessLevels } from './Reporting/ReportingAccessLevels'
import { ApplicationUserFormData, DeviceMonitoring } from './Types'
import {
    CxiDataEntitlement,
    CxIntegrationLicence
} from '../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { TelSysTypes } from '../TelSysConfiguration/Constants/Constants'
import { CXScoreLicences } from '../../NonReact/constants/CXScoreLicences'
import { CXRecordLicences } from '../../NonReact/constants/CXRecordLicences'
import { isBroadWorksForTranscribe, isTranscribeQualified } from '../../NonReact/utils/CxTranscribeUtils'

export const DEFAULT_PASSWORD_VALUE: string = '••••••'

type MapToFormStateFn = (
    data: ApplicationUser,
    setAdminPermissions: StateSetter<string[]>,
    setScope: StateSetter<Definable<FormPermissions>>,
    setEndpoint: StateSetter<Definable<FormEndpoint>>,
    setCallRec: StateSetter<Definable<string>>,
    setDefaultUsername: StateSetter<Definable<string>>
) => ApplicationUserFormData

const enterpriseReportingAccessMap: Record<string, ReportingAccessLevels> = {
    'WxC User': ReportingAccessLevels.ESSENTIALS,
    'WxC Agent': ReportingAccessLevels.CALL_CENTRE_AGENT,
    'WxC Supervisor': ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
    // MS Teams Business licences are returned under their business names but map onto the existing
    // Essentials / Professional / CCA / CCS reporting types (the form stores the base value).
    'Historic User': ReportingAccessLevels.ESSENTIALS,
    'Real-Time User': ReportingAccessLevels.PROFESSIONAL,
    Agent: ReportingAccessLevels.CALL_CENTRE_AGENT,
    Manager: ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
}

/**
 * Resolves a server {@code ReportingAccess} value to the base reporting level the form stores.
 *
 * <p>WebEx Calling and MS Teams Business licences are returned under their own business names
 * (e.g. {@code "Manager"}, {@code "Agent"}); these map onto the canonical
 * {@link ReportingAccessLevels} values used everywhere in the form. Any value that is already a
 * canonical level (or has no mapping) is returned unchanged.</p>
 *
 * <p>Both {@link mapToFormState} (for the reporting-level form field) and the modal's
 * {@code loadedReportingLevel} capture must use this single mapping so the loaded and current
 * values are compared in the same representation; otherwise a business licence is falsely
 * detected as a reporting-level change.</p>
 *
 * @param reportingAccess - The raw {@code Scope.ReportingAccess} value from the server.
 * @returns The canonical reporting level string.
 */
export const mapReportingAccessToLevel = (reportingAccess: string): string =>
    enterpriseReportingAccessMap[reportingAccess] || reportingAccess

/**
 * Map server data to form state for user.
 *
 * @param data - Data from the server.
 * @param setAdminPermissions - Set the state of the admin permissions.
 * @param setScope - Set the state of the Scope.
 * @param setEndpoint - Set the state of the Endpoint.
 * @param setCallRec - Set the state of the Call Recording integration.
 * @param setDefaultUsername - Set the state of the Default Username value.
 *
 * @returns - The application user form data.
 */
export const mapToFormState: MapToFormStateFn = (
    data: ApplicationUser,
    setAdminPermissions: StateSetter<string[]>,
    setScope: StateSetter<Definable<FormPermissions>>,
    setEndpoint: StateSetter<Definable<FormEndpoint>>,
    setCallRec: StateSetter<Definable<string>>,
    setDefaultUsername: StateSetter<Definable<string>>
): ApplicationUserFormData => {
    const {
        FullName,
        Username,
        EmailAddr,
        PasswordChgRequired,
        SynchroniseSupervisorPermissions,
        DefaultLang,
        EnableMFA,
        Advanced: {
            ReportUpdInterval,
            PromptForTerms,
            NotifyPasswordChgs,
            DisableMediaPlugIns,
            DisableDragDropFeatures,
            JavaScriptAuditOutput
        },
        LockOut,
        Scope: {
            ReportingAccess,
            CMEnvironmentID,
            TelSysID: [TelSysID],
            PartIDInTelSys,
            DeviceNumber,
            AgentNumber,
            CallRecIntID,
            CXILicenseType,
            CXScoreLicenseType,
            CXScoreEnabled,
            CXRLicenseType,
            CXRecordEnabled,
            CXTranscribeEnabled,
            CXIDataEntitlement
        },
        Permissions: { NonAdmin, Admin, Other },
        Endpoint: {
            TelSysID: EndpointTelSysID,
            PartIDInTelSys: EndpointPartIDInTelSys,
            DeviceNumber: EndpointDeviceNumber,
            AgentNumber: EndpointAgentNumber
        },
        TimeZone
    }: ApplicationUser = data
    const isReportReadonly: boolean =
        data.Permissions.NonAdmin.includes(REPORTS_READONLY) &&
        !REPORT_EDIT_PERMS.some((perm: string): boolean => data.Permissions.NonAdmin.includes(perm))

    // Set the default username.
    setDefaultUsername(Username)

    // Set the states that will get passed down to the child elements.
    setAdminPermissions(ArrayUtils.sortAlphabetically(Admin))

    // Set the permissions scope as the form value.
    setScope({
        cmenvironment: CMEnvironmentID,
        telephonyserver: IsVarEmpty(TelSysID) ? ALL_VALUE : TelSysID,
        partition: PartIDInTelSys.map((partID?: number): number => (IsVarEmpty(partID) ? ALL_VALUE : partID)),
        device: IsVarEmpty(DeviceNumber) ? SERVER_ALL_VALUE : DeviceNumber,
        agent: IsVarEmpty(AgentNumber) ? SERVER_ALL_VALUE : AgentNumber
    })

    // Set the endpoint scope as the form value.
    setEndpoint({
        telephonyserver: IsVarEmpty(EndpointTelSysID) ? ALL_VALUE : EndpointTelSysID,
        partition: IsVarEmpty(EndpointPartIDInTelSys) ? ALL_VALUE : EndpointPartIDInTelSys,
        device: IsVarEmpty(EndpointDeviceNumber) ? SERVER_NONE_VALUE : EndpointDeviceNumber,
        agent: IsVarEmpty(EndpointAgentNumber) ? SERVER_NONE_VALUE : EndpointAgentNumber
    })

    setCallRec(CallRecIntID && CallRecIntID !== SERVER_NONE_CRI ? CallRecIntID : SERVER_NONE_VALUE)

    const result: ApplicationUserFormData = {
        details: {
            username: Username ?? '',
            fullname: FullName ?? '',
            email: EmailAddr ?? '',
            defaultlanguage: DefaultLang ?? LANGUAGE_DEFAULT,
            password: DEFAULT_PASSWORD_VALUE,
            passwordchangerequired: PasswordChgRequired,
            enablemfa: !!EnableMFA,
            cxilicence: (CXILicenseType?.toUpperCase() as CxIntegrationLicence) ?? CxIntegrationLicence.NONE,
            timezone: isVarNotEmpty(TimeZone) ? TimeZone : DEFAULT_TIMEZONE,
            cxsSubscription: CXScoreLicenseType ?? SERVER_NONE_VALUE,
            cxsScoreEnabled: CXScoreEnabled ?? false,
            cxrSubscription: CXRLicenseType ?? SERVER_NONE_VALUE,
            cxrRecordEnabled: CXRecordEnabled ?? false,
            cxtTranscribeEnabled: CXTranscribeEnabled ?? false,
            cxidataentitlement:
                (CXIDataEntitlement?.toUpperCase() as CxiDataEntitlement) === CxiDataEntitlement.CXI_DATA
        },
        administrator: { rolepermissions: useMapPermissionsToFormValues(Admin) },
        permissions: {
            cmenvironment: CMEnvironmentID,
            telephonyserver: TelSysID,
            partition: PartIDInTelSys,
            device: DeviceNumber,
            agent: AgentNumber,
            synchronisesupervisorpermissions: SynchroniseSupervisorPermissions
        },
        extensionendpoint: {
            telephonyserver: EndpointTelSysID,
            partition: EndpointPartIDInTelSys,
            device: EndpointDeviceNumber,
            agent: EndpointAgentNumber
        },
        reporting: {
            reportinglevel: mapReportingAccessToLevel(ReportingAccess),
            reportingapi: NonAdmin.includes(BackendPermissionNames.REPORTING_API),
            readonly: isReportReadonly,
            enhancedfeatures: NonAdmin.includes(BackendPermissionNames.ENHANCED_FEATURES),
            callrecording: CallRecIntID && CallRecIntID !== SERVER_NONE_CRI ? CallRecIntID : SERVER_NONE_VALUE,
            reportTemplateUser: NonAdmin.includes(BackendPermissionNames.REPORT_TEMPLATE_USER)
        },
        settings: {
            lockedout: LockOut,
            updateintervals: `${ReportUpdInterval}`,
            concurrentsignins: Other.includes(BackendPermissionNames.CONCURRENT_SIGNINS),
            disablemediaplayer: DisableMediaPlugIns,
            draganddropfunctionality: DisableDragDropFeatures,
            javascriptoutput: JavaScriptAuditOutput,
            notifypasswordchange: NotifyPasswordChgs,
            prompttos: PromptForTerms,
            servicenotification: Other.includes(BackendPermissionNames.SERVICE_NOTIFICATION),
            authnotifications: Other.includes(BackendPermissionNames.AUTH_NOTIFICATIONS),
            newdevicenotifications: Other.includes(BackendPermissionNames.NEW_DEVICE_NOTIFICATIONS)
        },
        apiKeys: {
            name: EMPTY_STRING,
            expiry: null
        }
    }

    return result
}

/** No CX Score or CX Record on the user's device, as far as the user modal knows. */
const NO_DEVICE_MONITORING: DeviceMonitoring = { cxsScoreEnabled: false, cxrRecordEnabled: false }

/**
 * Whether a user can have Transcribe on, as far as the user modal can tell: by {@link isTranscribeQualified} on their
 * licences and the CX Score and CX Record flags their device loaded with. The backend validation reads the flags from
 * the device the user has before the save, not the one being saved, so the loaded flags stay the ones that count even
 * when the admin changes the scope or endpoint device.
 *
 * @param cxsLicence The user's CX Score licence, or undefined when none is selected.
 * @param cxrLicence The user's CX Record licence, or undefined when none is selected.
 * @param loadedDeviceMonitoring The flags on the device the user loaded with, or undefined for a new user.
 * @param isBroadWorks True when the user's TelSys is BroadWorks (or a Simulator emulating it).
 * @returns True when Transcribe can be offered.
 */
export const isTranscribeQualifiedForDevice = (
    cxsLicence: Definable<string>,
    cxrLicence: Definable<string>,
    loadedDeviceMonitoring: Definable<DeviceMonitoring>,
    isBroadWorks: boolean
): boolean => {
    const { cxsScoreEnabled, cxrRecordEnabled }: DeviceMonitoring = loadedDeviceMonitoring ?? NO_DEVICE_MONITORING

    return isTranscribeQualified({ cxsLicence, cxrLicence, cxsScoreEnabled, cxrRecordEnabled, isBroadWorks })
}

/**
 * Map from the form state to the server data.
 *
 * @param state - Form data.
 * @param defaultUsername - Whether we should take the
 * @param nonce - Nonce for the user's session
 * @param telSysType - The user's TelSys type.
 * @param loadedDeviceMonitoring - The flags on the device the user loaded with, or undefined for a new user.
 * @returns - The server state to send to the API endpoint.
 */
export const mapToServerState: (
    state: ApplicationUserFormData,
    defaultUsername: string,
    nonce?: string,
    telSysType?: Nullable<TelSysTypes>,
    loadedDeviceMonitoring?: DeviceMonitoring
) => AdminUserBody = (
    state: ApplicationUserFormData,
    defaultUsername: string,
    nonce?: Definable<string>,
    telSysType?: Nullable<TelSysTypes>,
    loadedDeviceMonitoring?: DeviceMonitoring
): AdminUserBody => {
    const updateIntervals: number = parseInt(state?.settings?.updateintervals ?? '', 10)
    const callRec: Nullable<string> = state?.reporting?.callrecording

    return {
        FullName: state?.details?.fullname ?? '',
        EmailAddr: state?.details?.email ?? '',
        Username: !IsStringEmpty(defaultUsername) ? defaultUsername : state?.details?.username ?? '',
        UsernameNew:
            !IsStringEmpty(defaultUsername) && state?.details?.username !== defaultUsername
                ? state?.details?.username
                : undefined,
        DefaultLang: state?.details?.defaultlanguage ?? LANGUAGE_DEFAULT,
        Password:
            state?.details?.password !== DEFAULT_PASSWORD_VALUE
                ? encrypt(state?.details?.password, nonce ?? null)
                : undefined,
        PasswordChgRequired: state?.details?.passwordchangerequired ?? false,
        LockOut: !!state?.settings?.lockedout,
        SendWelcomeEmail: state?.details?.sendwelcomeemail ?? false,
        EnableMFA: !!state?.details?.enablemfa,
        SynchroniseSupervisorPermissions: state?.permissions?.synchronisesupervisorpermissions ?? false,
        TimeZone: state?.details?.timezone === DEFAULT_TIMEZONE ? null : state?.details?.timezone,
        Scope: {
            CMEnvironmentID: state?.permissions?.cmenvironment ?? '',
            TelSysID: IsVarEmpty(state?.permissions?.telephonyserver)
                ? [ALL_VALUE]
                : [state?.permissions?.telephonyserver ?? ALL_VALUE],
            PartIDInTelSys: IsVarEmpty(state?.permissions?.partition)
                ? [ALL_VALUE]
                : state?.permissions?.partition ?? [ALL_VALUE],
            DeviceNumber: state?.permissions?.device ?? SERVER_ALL_VALUE,
            AgentNumber: state?.permissions?.agent ?? SERVER_ALL_VALUE,
            ReportingAccess: state?.reporting?.reportinglevel ?? SERVER_NONE_VALUE,
            CallRecIntID:
                telSysType !== TelSysTypes.MS_TEAMS && callRec && callRec !== SERVER_NONE_VALUE
                    ? callRec
                    : SERVER_NONE_CRI,
            CXILicenseType: state.details?.cxilicence ?? CxIntegrationLicence.NONE,
            // New additive CXI Data entitlement field.
            CXIDataEntitlement: state.details?.cxidataentitlement
                ? CxiDataEntitlement.CXI_DATA
                : CxiDataEntitlement.NONE,
            ServiceNotify: !!state?.settings?.servicenotification,
            DefaultReportCollection:
                IsVarEmpty(state?.reporting?.defaultreports) || state?.reporting?.defaultreports === SERVER_NONE_VALUE
                    ? null
                    : state?.reporting?.defaultreports,
            CXScoreLicenseType: state?.details?.cxsSubscription ?? SERVER_NONE_VALUE,
            // CXS/CXR monitoring is device-managed. The user modal forces the flag on for the
            // agent/user tiers (which require it) and otherwise leaves it untouched (null).
            CXScoreEnabled: state?.details?.cxsSubscription === CXScoreLicences.CXS_AGENT ? true : null,
            CXRLicenseType: state?.details?.cxrSubscription ?? SERVER_NONE_VALUE,
            CXRecordEnabled: state?.details?.cxrSubscription === CXRecordLicences.CXR_USER ? true : null,
            // The loaded value, so hiding the toggle (feature flag off, CME without CX Transcribe) leaves it as it was,
            // but never on for a user who qualifies for neither CX Score nor CX Record, by licence or by their device's
            // flags: the modal greys the toggle out then.
            CXTranscribeEnabled:
                !!state?.details?.cxtTranscribeEnabled &&
                isTranscribeQualifiedForDevice(
                    state?.details?.cxsSubscription,
                    state?.details?.cxrSubscription,
                    loadedDeviceMonitoring,
                    isBroadWorksForTranscribe(telSysType)
                )
        },
        Endpoint: {
            TelSysID:
                IsVarEmpty(state?.extensionendpoint?.telephonyserver) ||
                state?.extensionendpoint?.telephonyserver === ALL_VALUE
                    ? null
                    : state?.extensionendpoint?.telephonyserver,
            PartIDInTelSys:
                IsVarEmpty(state?.extensionendpoint?.partition) || state?.extensionendpoint?.partition === ALL_VALUE
                    ? null
                    : state?.extensionendpoint?.partition,
            DeviceNumber:
                state?.extensionendpoint?.device === SERVER_NONE_VALUE ? null : state?.extensionendpoint?.device,
            AgentNumber: state?.extensionendpoint?.agent === SERVER_NONE_VALUE ? null : state?.extensionendpoint?.agent
        },
        Advanced: {
            ReportUpdInterval: Number.isNaN(updateIntervals) ? ALL_VALUE : updateIntervals,
            DisableDragDropFeatures: state?.settings?.draganddropfunctionality,
            DisableMediaPlugIns: state?.settings?.disablemediaplayer,
            JavaScriptAuditOutput: state?.settings?.javascriptoutput,
            NotifyPasswordChgs: state?.settings?.notifypasswordchange,
            PromptForTerms: state?.settings?.prompttos
        },
        Permissions: {
            Admin: mapValuesToPermissions(state?.administrator?.rolepermissions),
            NonAdmin: mapNonAdminPermissions(state),
            Other: mapOtherPermissions(state)
        }
    }
}
